import type {
  ReviewSubmissionResponse,
  ReviewsResponse,
} from "../../shared/types";

async function requestJson<T>(input: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 15_000);
  let response: Response;
  try {
    response = await fetch(input, {
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
      ...init,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("The request timed out. Please try again.");
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }

  const payload = await response.json().catch(() => ({})) as { error?: string } & T;
  if (!response.ok) {
    throw new Error(payload.error ?? `Request failed with status ${response.status}`);
  }
  return payload;
}

export function signup(email: string, password: string, turnstileToken?: string): Promise<{ ok: true }> {
  return requestJson("/api/auth/signup", {
    method: "POST",
    body: JSON.stringify({ email, password, turnstileToken }),
  });
}

export function login(email: string, password: string, turnstileToken?: string): Promise<{ ok: true }> {
  return requestJson("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, turnstileToken }),
  });
}

export function logout(): Promise<{ ok: true }> {
  return requestJson("/api/auth/logout", {
    method: "POST",
  });
}

export function verifyAccount(token: string): Promise<{ ok: true }> {
  return requestJson(`/api/auth/verify?token=${encodeURIComponent(token)}`);
}

export function requestPasswordReset(email: string, turnstileToken?: string): Promise<{ ok: true; message: string }> {
  return requestJson("/api/auth/password-reset/request", {
    method: "POST",
    body: JSON.stringify({ email, turnstileToken }),
  });
}

export function confirmPasswordReset(token: string, password: string): Promise<{ ok: true }> {
  return requestJson("/api/auth/password-reset/confirm", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

export function fetchDynamicReviews(courseId: string): Promise<ReviewsResponse> {
  return requestJson(`/api/courses/${courseId}/reviews`);
}

export function createReview(
  courseId: string,
  payload: {
    professor: string;
    quarter: string;
    median?: "A" | "A-" | "B+" | "B" | "B-" | "C+" | "Unsure" | null;
    comments: string;
    turnstileToken?: string;
  },
): Promise<ReviewSubmissionResponse> {
  const normalizedPayload = {
    professor: payload.professor.trim(),
    quarter: payload.quarter.trim().toUpperCase(),
    median: payload.median === "Unsure" ? null : (payload.median ?? null),
    comments: payload.comments.trim(),
    turnstileToken: payload.turnstileToken,
  };

  return requestJson(`/api/courses/${courseId}/reviews`, {
    method: "POST",
    body: JSON.stringify(normalizedPayload),
  });
}

export function moderateReview(reviewId: string, visibility: "visible" | "hidden"): Promise<{ ok: true }> {
  return requestJson(`/api/admin/reviews/${reviewId}/hide`, {
    method: "POST",
    body: JSON.stringify({ visibility }),
  });
}
